import { useState } from "react"
import { toast } from "react-toastify"
import {
  useUpdateAdSetBudgetMutation,
  useUpdateCampaignBudgetMutation,
} from "~/ui-rtk/api/paidMediaApi"
import {
  BudgetType,
  type AdSet,
  type Campaign,
} from "~/ui-rtk/api/types/paid-media.types"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import {
  describeBudget,
  formatMoney,
  ownBudget,
  parseAmountToCents,
} from "./budget-utils"

type Props =
  | { kind: "campaign"; entity: Campaign }
  | { kind: "adset"; entity: AdSet }

/**
 * One budget row: current value, new amount input, Save. Writes go straight
 * to Meta through PATCH /paid-media/meta/{campaigns|adsets}/:id/budget; the
 * RTK cache tags refetch the list so the new value shows right after.
 */
export default function BudgetEditor({ kind, entity }: Props) {
  const budget = ownBudget(entity)
  const [value, setValue] = useState(budget ? budget.amount.toFixed(2) : "")
  const [updateCampaignBudget, campaignState] =
    useUpdateCampaignBudgetMutation()
  const [updateAdSetBudget, adSetState] = useUpdateAdSetBudgetMutation()
  const isSaving = campaignState.isLoading || adSetState.isLoading

  if (!budget) {
    return (
      <p className="text-sm text-neutral">
        No budget on this {kind === "campaign" ? "campaign" : "ad set"}.
      </p>
    )
  }

  const cents = parseAmountToCents(value)
  const unchanged = cents === Math.round(budget.amount * 100)

  const handleSave = async () => {
    if (cents === null || unchanged) return
    try {
      if (kind === "campaign") {
        await updateCampaignBudget({
          platform: "meta",
          campaignId: entity.id,
          data: {
            budgetType:
              budget.type === "DAILY" ? BudgetType.DAILY : BudgetType.LIFETIME,
            budgetAmount: cents,
          },
        }).unwrap()
      } else {
        await updateAdSetBudget({
          platform: "meta",
          adsetId: entity.id,
          data:
            budget.type === "DAILY"
              ? { dailyBudget: cents }
              : { lifetimeBudget: cents },
        }).unwrap()
      }
      toast.success(
        `Budget updated on Meta: ${entity.name} ${formatMoney(budget.amount)} → ${formatMoney(cents / 100)}`,
      )
    } catch (error) {
      toast.error(getServerErrorMessage(error, "Failed to update budget"))
    }
  }

  return (
    <form
      className="flex flex-wrap items-center gap-3"
      onSubmit={e => {
        e.preventDefault()
        void handleSave()
      }}
    >
      <span className="text-sm text-neutral min-w-36">
        Current: <span className="text-white">{describeBudget(entity)}</span>
      </span>
      <label className="flex items-center gap-2 text-sm text-neutral">
        New {budget.type === "DAILY" ? "daily" : "lifetime"} budget ($)
        <input
          type="number"
          inputMode="decimal"
          min="0.01"
          step="0.01"
          value={value}
          onChange={e => setValue(e.target.value)}
          className="input input-sm input-bordered w-32 text-white bg-basic-black"
          aria-label={`New budget for ${entity.name}`}
        />
      </label>
      <button
        type="submit"
        className="btn btn-sm btn-primary"
        disabled={cents === null || unchanged || isSaving}
      >
        {isSaving ? "Saving…" : "Save to Meta"}
      </button>
    </form>
  )
}
